import apiService from '@/store/apiService';

export const miscEndpoints = {
    noticeboards: '/portal/noticeboard/',
    noticeboardSubscriptionStatus: '/portal/noticeboard/subscription/status/',
    noticeboardSubscribe: '/portal/noticeboard/subscription/subscribe/',
    noticeboardUnsubscribe: '/portal/noticeboard/subscription/unsubscribe/',
    noticeboardConfirmUnsubscribe: '/portal/noticeboard/subscription/confirm-unsubscribe/',
    supportRequest: '/portal/support/request/',
};

export const MiscTagsTypes = [
    'Noticeboards'
];

export interface PaginationResponse<T> {
    count: number;
    next: string | null;
    previous: string | null;
    results: T[];
}

export interface NoticeboardSubscriptionStatusResponse {
    noticeboard_email_subscribed: boolean;
}

type NoticeboardsApiResponse = PaginationResponse<NoticeboardResponse> | NoticeboardResponse[];

export type NoticeboardResponse = {
    id: number;
    markdown?: string;
    posted_by?: string;
    portal_id?: number | null;
    cdate: string;
    content?: string | null;
    title: string;
    app?: string | null;
    tags?: string | null;
    user_id?: number | null;
    expiry: null | string;
    login_display?: boolean | null;
    release_version?: string | null;
    release_date?: string | null;
    effective_date?: string | null;
    ticket_reference?: string | null;
    ticket_url?: string | null;
    category?: string | null;
    severity?: string | null;
    components?: string[] | Record<string, unknown> | string | null;
    summary?: string | null;
    required_actions?: string | null;
    reference?: string | null;
    support_contact?: string | null;
    status?: string | null;
    published_at?: string | null;
    impact?: string | null;
    performed_tests?: string | null;
    downtime?: string | null;
    timezone?: string | null;
    rollback_plan?: string | null;
    related_notes?: string | null;
};

const MiscApi = apiService
    .enhanceEndpoints({ addTagTypes: MiscTagsTypes })
    .injectEndpoints({
        endpoints: (build) => ({
            getNoticeboards: build.query<PaginationResponse<NoticeboardResponse>, { offset: number; search?: string; cdate_after?: string; cdate_before?: string; }>({
                query: ({ offset, search, cdate_after, cdate_before }) => ({
                    url: miscEndpoints.noticeboards,
                    method: 'GET',
                    params: {
                        offset,
                        ...(search ? { search } : {}),
                        ...(cdate_after ? { cdate_after } : {}),
                        ...(cdate_before ? { cdate_before } : {}),
                    },
                }),
                transformResponse: (response: NoticeboardsApiResponse): PaginationResponse<NoticeboardResponse> => {
                    if (Array.isArray(response)) {
                        return {
                            count: response.length,
                            next: null,
                            previous: null,
                            results: response,
                        };
                    }

                    return response;
                },
                providesTags: ['Noticeboards']
            }),
            getNoticeboard: build.query<NoticeboardResponse, number>({
                query: (id) => ({
                    url: `${miscEndpoints.noticeboards}${id}/`,
                    method: 'GET',
                }),
                providesTags: ['Noticeboards'],
            }),
            raiseSupportRequest: build.mutation<void, { subject: string; message: string; }>({
                query: ({ subject, message }) => ({
                    url: miscEndpoints.supportRequest,
                    method: 'POST',
                    body: { subject, message },
                }),
            }),
            getNoticeboardSubscriptionStatus: build.query<NoticeboardSubscriptionStatusResponse, void>({
                query: () => ({
                    url: miscEndpoints.noticeboardSubscriptionStatus,
                    method: 'GET',
                }),
                providesTags: ['Noticeboards'],
            }),
            subscribeToNoticeboardEmails: build.mutation<NoticeboardSubscriptionStatusResponse, void>({
                query: () => ({
                    url: miscEndpoints.noticeboardSubscribe,
                    method: 'POST',
                }),
                invalidatesTags: ['Noticeboards'],
            }),
            unsubscribeFromNoticeboardEmails: build.mutation<NoticeboardSubscriptionStatusResponse, void>({
                query: () => ({
                    url: miscEndpoints.noticeboardUnsubscribe,
                    method: 'POST',
                }),
                invalidatesTags: ['Noticeboards'],
            }),
            confirmNoticeboardUnsubscribe: build.mutation<NoticeboardSubscriptionStatusResponse, { token: string; }>({
                query: ({ token }) => ({
                    url: miscEndpoints.noticeboardConfirmUnsubscribe,
                    method: 'POST',
                    body: { token },
                }),
                invalidatesTags: ['Noticeboards'],
            }),
        })
    });

export const {
    useGetNoticeboardsQuery,
    useGetNoticeboardQuery,
    useRaiseSupportRequestMutation,
    useGetNoticeboardSubscriptionStatusQuery,
    useSubscribeToNoticeboardEmailsMutation,
    useUnsubscribeFromNoticeboardEmailsMutation,
    useConfirmNoticeboardUnsubscribeMutation,
} = MiscApi;
